import React, { useEffect, useState } from "react";
import { Download, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, brl, API } from "@/lib/api";

const STATUS = ["pendente", "aguardando_pagamento", "pago", "em_producao", "enviado", "entregue", "cancelado"];

export default function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    await api.patch(`/orders/${id}`, { status });
    toast.success("Status atualizado");
    load();
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "pedidos.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao exportar"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-[var(--text-muted)]">{orders.length} pedido(s)</p>
        <Button onClick={exportCsv} data-testid="export-orders" variant="outline" className="border-[var(--border)]"><Download size={16} className="mr-1" /> Exportar CSV</Button>
      </div>
      {orders.length === 0 ? (
        <div className="card-surface p-10 text-center text-[var(--text-muted)]">Nenhum pedido ainda.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="card-surface p-4" data-testid={`order-${o.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-bold">{o.buyer?.name} <span className="text-xs text-[var(--text-muted)]">#{o.id.slice(0, 8)}</span></div>
                  <div className="text-xs text-[var(--text-muted)]">{new Date(o.created_at).toLocaleString("pt-BR")} · {o.buyer?.shipping_method}</div>
                </div>
                <div className="text-[var(--accent)] font-bold">{brl(o.total)}{Number(o.delivery_fee || 0) > 0 && <div className="text-xs text-[var(--text-muted)] font-normal">inclui entrega {brl(o.delivery_fee)}</div>}</div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded ${o.payment_status === "approved" || o.status === "pago" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>{o.payment_status || "pendente"}</span>
                  <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                    <SelectTrigger className="w-40 bg-[var(--bg-secondary)]" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <button onClick={() => setExpanded(expanded === o.id ? null : o.id)}>{expanded === o.id ? <ChevronUp /> : <ChevronDown />}</button>
                </div>
              </div>
              {expanded === o.id && (
                <div className="mt-4 border-t border-[var(--border)] pt-4 grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-semibold mb-2">Itens</div>
                    {o.items?.map((it, i) => <div key={i} className="text-[var(--text-muted)]">{it.quantity}x {it.name} — Tam {it.size} ({brl(it.unit_price)})</div>)}
                    <div className="mt-2 text-[var(--text-muted)]">Produtos: {brl(o.subtotal || o.total)} · Entrega: {brl(o.delivery_fee || 0)} · Total: {brl(o.total)}</div>
                  </div>
                  <div className="text-[var(--text-muted)]">
                    <div className="font-semibold text-[var(--text-main)] mb-2">Comprador</div>
                    <div>CPF: {o.buyer?.cpf}</div>
                    <div>E-mail: {o.buyer?.email}</div>
                    <div>Tel: {o.buyer?.phone}</div>
                    <div>Forma: {o.buyer?.shipping_method}</div>
                    {o.buyer?.address && <div>Endereço: {o.buyer.address}</div>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
